import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Plugin, ChartOptions } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import '../../../../lib/chartTheme';
import { TooltipItem } from 'chart.js';
import Button from '../../../../components/Button/Button';
import { useLanguage } from '../../../../contexts/LanguageContext';

import styles from './BookingsRevenueChart.module.css';
import { readToken, useTokenColors } from '../../../../lib/useTokenColors';

// AB#2193 — Chart.js draws on canvas: series colours resolved from tokens.
const CHART_TOKENS = ['primary', 'accent-orchid-fg'] as const;

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const roundedBarPlugin: Plugin<'bar'> = {
    id: 'roundedBarPlugin',
    afterDatasetsDraw: (chart) => {
        const ctx = chart.ctx;

        chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);

            meta.data.forEach((bar) => {
                const { x, y, height } = bar.getProps(['x', 'y', 'height'], true);
                const radius = 4;

                ctx.save();
                ctx.beginPath();

                const halfWidth = 5;
                const top = y;
                const bottom = y + height;
                const left = x - halfWidth;
                const right = x + halfWidth;

                ctx.moveTo(left + radius, top);
                ctx.lineTo(right - radius, top);
                ctx.arcTo(right, top, right, top + radius, radius);
                ctx.lineTo(right, bottom - radius);
                ctx.arcTo(right, bottom, right - radius, bottom, radius);
                ctx.lineTo(left + radius, bottom);
                ctx.arcTo(left, bottom, left, bottom - radius, radius);
                ctx.lineTo(left, top + radius);
                ctx.arcTo(left, top, left + radius, top, radius);

                ctx.closePath();
                ctx.clip();

                // AB#2193 — read at draw time so the bars follow the theme (legend dots use the same tokens)
                const backgroundColor = [readToken('primary'), readToken('accent-orchid-fg')];
                const color = Array.isArray(backgroundColor) ? backgroundColor[datasetIndex] : backgroundColor;
                ctx.fillStyle = color as string;
                ctx.fill();

                ctx.restore();
            });
        });
    },
};

interface Props {
    labels: string[];
    bookings: number[];
    amounts: number[];
    totalRevenue: number;
    reservationRate: number; // percent
    /** O1 UAT — false until the first load: totals show "—", never 0. */
    loaded?: boolean;
    currency: string; // AB#1982 (AC-3) — tenant currency (JOD/AED)
}

const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
        x: {
            grid: {
                display: false,
            },
            ticks: {
                display: true,
            },
            border: {
                display: false,
            },
        },
        y: {
            grid: {
                display: true,
                color: () => readToken('chart-grid'),
            },
            ticks: {
                display: true,
                padding: 15,
                callback: (value: number | string, index: number, values: any[]) => {
                    return index % 2 === 0 ? value : '';
                },
            },
            border: {
                display: false,
            },
        },
    },
    plugins: {
        legend: {
            display: false,
        },
        tooltip: {
            callbacks: {
                title: (tooltipItems: TooltipItem<'bar'>[]) => {
                    return `${tooltipItems[0].label}`;
                },
                label: (tooltipItem: TooltipItem<'bar'>) => {
                    return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
                },
                labelColor: (tooltipItem: TooltipItem<'bar'>) => {
                    return {
                        borderColor: `${tooltipItem.dataset.hoverBorderColor}`,
                        backgroundColor: `${tooltipItem.dataset.hoverBorderColor}`,
                        borderWidth: 1,
                        borderRadius: 1,
                    };
                },
            },
            backgroundColor: 'rgba(0,0,0,0.7)',
            titleFont: { size: 16 },
            bodyFont: { size: 14 },
            footerFont: { size: 12 },
        },
    },
    layout: {},
};

const BookingsRevenueChart: React.FC<Props> = ({ labels, bookings, amounts, totalRevenue, reservationRate, currency, loaded = true }) => {
    const { t } = useLanguage();
    const tc = useTokenColors(CHART_TOKENS);
    // Define the series labels once via t() so the chart.js tooltip
    // (reads dataset.label at line ~114) and the JSX legend below always match.
    const bookingsLabel = t('dashboard.chart.bookings');
    const amountLabel = t('dashboard.chart.amount');
    const data = {
        labels,
        datasets: [
            {
                label: bookingsLabel,
                borderRadius: 18,
                data: bookings,
                hoverBorderColor: tc.primary,
                backgroundColor: 'transparent',
                hoverBackgroundColor: 'transparent',
                barThickness: 15,
                maxBarThickness: 40,
            },
            {
                label: amountLabel,
                borderRadius: 18,
                data: amounts,
                hoverBorderColor: tc['accent-orchid-fg'],
                backgroundColor: 'transparent',
                hoverBackgroundColor: 'transparent',
                barThickness: 15,
                maxBarThickness: 40,
            },
        ],
    };
    return (
        <div className={styles.chartContainer}>
            <div className={styles.header}>
                <h2 className='headerText'>Bookings & Revenue</h2>
                <Button label='Export' noAppearance={true} icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path style={{ stroke: 'var(--text-tertiary)' }} d="M5.66675 7.70679L9.12873 11.1909L12.5934 7.70679" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    <path style={{ stroke: 'var(--text-tertiary)' }} fill-rule="evenodd" clip-rule="evenodd" d="M10.0467 17.0294C4.81074 17.698 0.404078 13.2656 1.06608 7.99875C1.51741 4.41266 4.39607 1.51803 7.96074 1.06599C13.1901 0.403367 17.5901 4.82512 16.9354 10.0872C16.4894 13.6753 13.6121 16.5747 10.0467 17.0294Z" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                } iconPosition='left' size='small' />
            </div>
            <div className={styles.detailsContainer}>
                <div className={styles.textContainer}>
                    <span>
                        <h3 className='headerText' >{loaded ? totalRevenue.toLocaleString() : '—'}<span>{currency}</span></h3>
                        <p className={styles.subText}>{t('dashboard.chart.totalRevenue')}</p>
                    </span>
                    <span>
                        <h3 className='headerText' >{loaded ? `${reservationRate}%` : '—'}</h3>
                        <p className={styles.subText}>{t('dashboard.chart.reservationRate')}</p>
                    </span>
                </div>
                <div className={styles.legendRow}>
                    <span className={styles.legendItem}>
                        <span className={`${styles.legendDot} ${styles.legendDotBookings}`} />
                        {bookingsLabel}
                    </span>
                    <span className={styles.legendItem}>
                        <span className={`${styles.legendDot} ${styles.legendDotAmount}`} />
                        {amountLabel}
                    </span>
                </div>
            </div>
            <div className={styles.chartWrapper}>
                <Bar data={data} options={options} plugins={[roundedBarPlugin]} />
            </div>
        </div>
    );
};

export default BookingsRevenueChart;
